import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {ScrollRestoration | null} */
  #previousScrollRestoration = null;

  /** @type {number | undefined} */
  #scrollRestorationTimeout;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen);
    window.addEventListener('pageshow', this.#handlePageShow);
    window.addEventListener('pagehide', this.#resumeScrollRestoration);
    document.addEventListener('cart:add-start', this.#handleCartAddStart);
    document.addEventListener('cart:add-end', this.#handleCartAddEnd);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen);
    window.removeEventListener('pageshow', this.#handlePageShow);
    window.removeEventListener('pagehide', this.#resumeScrollRestoration);
    document.removeEventListener('cart:add-start', this.#handleCartAddStart);
    document.removeEventListener('cart:add-end', this.#handleCartAddEnd);
    clearTimeout(this.#busyTimeout);
    clearTimeout(this.#scrollRestorationTimeout);
    this.#resumeScrollRestoration();
    this.#historyAbortController?.abort();
  }

  /**
   * After a bfcache restore (mobile back/forward navigation) the restored DOM
   * may predate cart changes made on other pages: mark it as suspect — the
   * next open stays masked until verified — and start a re-sync right away.
   * @param {PageTransitionEvent} event
   */
  #handlePageShow = (event) => {
    if (!event.persisted) return;

    this.#domSuspect = true;

    if (this.refs.dialog?.open) {
      this.refs.dialog.setAttribute('aria-busy', 'true');
      this.#syncWithServer().finally(this.#releaseBusy);
    } else {
      this.#syncWithServer();
    }
  };

  /** @type {boolean} */
  #domSuspect = false;

  /**
   * Monotonically increasing cart-add generation. A sync started for an older
   * generation is never allowed to certify the drawer after a newer add began.
   * @type {number}
   */
  #cartStateGeneration = 0;

  /**
   * Generation for which a successful add has established the invariant that
   * the cart must not be accepted as empty without corroboration.
   * @type {number}
   */
  #nonEmptyExpectedGeneration = -1;

  /**
   * Verifies the drawer content on open. When the DOM is suspect (bfcache
   * restore, unconfirmed add), mask the content until the verification
   * settles so stale content is never readable; otherwise verify silently.
   */
  #handleDialogOpen = () => {
    const mustMask = this.#domSuspect;

    if (mustMask) this.refs.dialog?.setAttribute('aria-busy', 'true');

    this.#syncWithServer().finally(() => {
      if (mustMask) this.#releaseBusy();
    });
  };

  /** @type {number | undefined} */
  #busyTimeout;

  /** @type {number} */
  #pendingAddCount = 0;

  /** @type {boolean} */
  #batchHadConcurrency = false;

  /** Shows a busy overlay while add-to-cart requests are in flight. */
  #handleCartAddStart = () => {
    // Every add starts a new state generation. Any sync already in flight may
    // finish, but it can no longer vouch for this newer cart state.
    this.#cartStateGeneration += 1;
    this.#nonEmptyExpectedGeneration = -1;

    // Overlapping adds (mobile double-tap): one confirmed response must not
    // vouch for the whole batch — keep the batch suspect until the final
    // verification.
    if (this.#pendingAddCount > 0) this.#batchHadConcurrency = true;

    this.#pendingAddCount += 1;

    // Until this add is confirmed (CartAddEvent carrying valid drawer HTML)
    // or a re-sync completes, the drawer DOM can no longer be trusted: a
    // lost response would leave it stale without any error surfacing.
    this.#domSuspect = true;

    this.refs.dialog?.setAttribute('aria-busy', 'true');

    // Safety net: never leave the overlay up if requests hang.
    clearTimeout(this.#busyTimeout);
    this.#busyTimeout = setTimeout(() => {
      this.#pendingAddCount = 0;
      this.#releaseBusy();
    }, 10000);
  };

  #handleCartAddEnd = () => {
    if (this.#pendingAddCount > 0) this.#pendingAddCount -= 1;

    // Another add is still in flight: keep the overlay up for it.
    if (this.#pendingAddCount > 0) return;

    clearTimeout(this.#busyTimeout);
    this.#batchHadConcurrency = false;

    if (this.#domSuspect) {
      // No confirmed render for this batch (error, lost response, missing
      // sections): hold the overlay until the server verification settles.
      this.#syncWithServer().finally(this.#releaseBusy);
    } else {
      // Confirmed render: unmask immediately, then verify silently in the
      // background (deduplicated for the current cart generation).
      this.#releaseBusy();
      this.#syncWithServer();
    }
  };

  /** Removes the busy overlay unless add requests are still in flight. */
  #releaseBusy = () => {
    if (this.#pendingAddCount > 0) return;
    this.refs.dialog?.removeAttribute('aria-busy');
  };

  /** @type {Promise<void> | null} */
  #syncPromise = null;

  /** @type {number} */
  #syncGeneration = -1;

  /**
   * Deduplicates syncs only within the same cart generation. If a new add
   * starts while a sync is running, the new generation gets its own sync and
   * the stale one cannot clear #domSuspect.
   */
  #syncWithServer = () => {
    const generation = this.#cartStateGeneration;

    if (this.#syncPromise && this.#syncGeneration === generation) {
      return this.#syncPromise;
    }

    const syncPromise = this.#performSync(generation).finally(() => {
      if (this.#syncPromise === syncPromise) {
        this.#syncPromise = null;
        this.#syncGeneration = -1;
      }
    });

    this.#syncPromise = syncPromise;
    this.#syncGeneration = generation;
    return syncPromise;
  };

  /**
   * Fetches the cart using explicit no-store semantics plus a request nonce.
   * The nonce is defensive against intermediary/browser caching; correctness
   * still comes from generation checks and server/DOM corroboration.
   * @returns {Promise<Response>}
   */
  #fetchServerCart = () => {
    const url = new URL(`${Theme.routes.cart_url}.js`, window.location.origin);
    url.searchParams.set('_drawer_sync', `${Date.now()}-${this.#cartStateGeneration}`);

    return fetch(url.toString(), {
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    });
  };

  /**
   * Compares server cart lines with the currently rendered drawer DOM.
   * @param {{ item_count?: number, items?: Array<{ key: string, quantity: number }> }} cart
   * @returns {{ matches: boolean, domCount: number }}
   */
  #compareCartToDom = (cart) => {
    const serverPairs = (cart.items ?? []).map((item) => `${item.key}@${item.quantity}`);
    const domPairs = Array.from(this.querySelectorAll('.cart-items__table-row[data-key]'), (row) => {
      const quantity = row.querySelector('input[name="updates[]"]')?.getAttribute('value');
      return `${row.getAttribute('data-key')}@${quantity ?? '?'}`;
    });

    // Rows without a quantity input (e.g. bundle components) can't expose
    // their quantity: fall back to comparing line keys only.
    const withQuantities = !domPairs.some((pair) => pair.endsWith('@?'));
    const toSignature = (/** @type {string[]} */ pairs) =>
      pairs
        .map((pair) => (withQuantities ? pair : pair.slice(0, pair.lastIndexOf('@'))))
        .sort()
        .join(',');

    const domCount = parseInt(this.querySelector('[ref="cartItemCount"]')?.textContent ?? '0', 10);
    const serverSignature = `${cart.item_count ?? 0}|${toSignature(serverPairs)}`;
    const domSignature = `${domCount}|${toSignature(domPairs)}`;

    return { matches: serverSignature === domSignature, domCount };
  };

  /**
   * The drawer DOM is only updated through cart events; a lost response or a
   * stale bfcache restore leaves it showing the wrong content. A successful
   * add also establishes a non-empty invariant: one contradictory empty cart
   * read is never enough to certify an empty drawer.
   * @param {number} generation - Cart generation this sync is validating
   */
  #performSync = async (generation) => {
    try {
      let response = await this.#fetchServerCart();
      if (!response.ok) return;
      let cart = await response.json();

      // A newer add began while this request was in flight. This result is no
      // longer authoritative for the current drawer state.
      if (generation !== this.#cartStateGeneration) return;

      const expectsNonEmpty = this.#nonEmptyExpectedGeneration === generation;
      const sectionId = this.querySelector('cart-items-component')?.getAttribute('data-section-id');

      if (expectsNonEmpty && (cart.item_count ?? 0) === 0) {
        // Contradiction: an add was confirmed, yet both a stale DOM and a
        // transient cart read can say zero. Force a genuinely fresh section
        // round-trip first (1A guarantees latest-render-wins), then corroborate
        // with a second uncached cart read. No arbitrary sleep is required.
        if (!sectionId) return;

        await sectionRenderer.renderSection(sectionId, { cache: false });
        if (generation !== this.#cartStateGeneration) return;

        response = await this.#fetchServerCart();
        if (!response.ok) return;
        cart = await response.json();

        if (generation !== this.#cartStateGeneration) return;

        if ((cart.item_count ?? 0) === 0) {
          // Two server round-trips still contradict the confirmed add. Do not
          // certify the empty drawer; leave it suspect so the next open checks
          // again instead of turning a transient zero into trusted UI state.
          this.#domSuspect = true;
          return;
        }
      }

      let comparison = this.#compareCartToDom(cart);

      if (comparison.matches) {
        if (generation !== this.#cartStateGeneration) return;

        this.#domSuspect = false;
        if (expectsNonEmpty && (cart.item_count ?? 0) > 0) {
          this.#nonEmptyExpectedGeneration = -1;
        }
        return;
      }

      if (!sectionId) return;

      await sectionRenderer.renderSection(sectionId, { cache: false });
      if (generation !== this.#cartStateGeneration) return;

      // Do not blindly trust that the render fixed the DOM. Verify the result
      // against the same authoritative cart snapshot before lowering suspect.
      comparison = this.#compareCartToDom(cart);
      if (!comparison.matches) {
        this.#domSuspect = true;
        return;
      }

      this.#domSuspect = false;
      if (expectsNonEmpty && (cart.item_count ?? 0) > 0) {
        this.#nonEmptyExpectedGeneration = -1;
      }
    } catch {
      // Offline or request aborted: keep the current drawer state so the
      // next open re-verifies (#domSuspect stays raised on suspect paths).
    }
  };

  /**
   * The drawer's history entry is pushed while the page is scroll-locked with
   * `body { position: fixed }`, so the scroll offset the browser records for
   * the round-trip is the top of the document. Left on `auto`, the traversal
   * back re-applies that zero a moment after `closeDialog` has restored the
   * real position - the page silently jumps to the top, a few frames after
   * the drawer is gone. Suspending restoration for the round-trip leaves the
   * position to `closeDialog`, which is the only code that knows it.
   *
   * Suspending before `pushState` matters: the pushed entry inherits the
   * current entry's mode, so both ends of the round-trip are then `manual`.
   */
  #suspendScrollRestoration = () => {
    clearTimeout(this.#scrollRestorationTimeout);

    if (this.#previousScrollRestoration != null || !('scrollRestoration' in history)) return;

    this.#previousScrollRestoration = history.scrollRestoration;
    history.scrollRestoration = 'manual';
  };

  /**
   * Hands scroll restoration back to the browser so ordinary back/forward
   * navigation keeps restoring the page.
   */
  #resumeScrollRestoration = () => {
    const previous = this.#previousScrollRestoration;
    if (previous == null) return;

    this.#previousScrollRestoration = null;
    history.scrollRestoration = previous;
  };

  /**
   * `history.back()` resolves asynchronously: resuming in the same task would
   * hand restoration back before the traversal has read it.
   */
  #scheduleScrollRestorationResume = () => {
    if (this.#previousScrollRestoration == null) return;

    clearTimeout(this.#scrollRestorationTimeout);
    this.#scrollRestorationTimeout = setTimeout(this.#resumeScrollRestoration, 600);
  };

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    this.#suspendScrollRestoration();

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }

    this.#scheduleScrollRestorationResume();
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number }, data?: { didError?: boolean, sections?: Record<string, string> } }>} event
   */
  #handleCartAdd = (event) => {
    // A rejected add (sold out, quantity limit, rate limit) must not open the
    // drawer over its "empty cart" state: the error message shown under the
    // add-to-cart button would be hidden behind the drawer.
    if (event.detail?.data?.didError) return;

    // Only a real product-form add establishes the non-empty invariant.
    // CartUpdateEvent shares the same event name, and quick-order-list also
    // emits CartAddEvent for quantity changes/removals, so type alone is not
    // sufficient to distinguish a successful add from other cart mutations.
    const isProductFormAdd =
      event instanceof CartAddEvent && event.detail?.data?.source === 'product-form-component';

    if (isProductFormAdd) {
      this.#nonEmptyExpectedGeneration = this.#cartStateGeneration;
    } else {
      this.#nonEmptyExpectedGeneration = -1;
    }

    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    // The add succeeded but the response carried no usable section HTML for
    // this drawer — Shopify can return `sections: null` or, per section,
    // `sections: { "<id>": null }` even on success. The drawer would open on
    // stale content while the fallback render is in flight: mask it and heal.
    const sectionId = this.querySelector('cart-items-component')?.getAttribute('data-section-id');
    const sectionHtml = sectionId ? event.detail?.data?.sections?.[sectionId] : null;
    if (!sectionHtml) {
      this.refs.dialog?.setAttribute('aria-busy', 'true');
      this.#syncWithServer().finally(this.#releaseBusy);
    } else if (!this.#batchHadConcurrency) {
      // Valid drawer HTML: the synchronous morph runs in this same event
      // dispatch, before the deferred showModal — the DOM is trustworthy.
      // The later server sync still corroborates the successful-add invariant.
      this.#domSuspect = false;
    }

    this.#announceCartCount(event.detail.resource?.item_count);
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
